'use client';
import React, { ReactNode, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2Icon } from 'lucide-react';
import { createRoot, Root } from 'react-dom/client';

export type ModalDirectiveConfig = {
  title: ReactNode;
  content: ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
  onOk?: () => Promise<void>;
  onCancel?: () => Promise<void>;
  okText?: string;
  cancelText?: string;
  showCancel?: boolean;
};

function InfoModal({
  onOpenChange,
  title,
  content,
  onOk,
  okText,
  cancelText,
  onCancel,
  showCancel,
}: ModalDirectiveConfig) {
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    onOpenChange?.(false);
  };

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="[&>button]:hidden"
      >
        <DialogHeader className="flex flex-col gap-1">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div>{content}</div>
        <DialogFooter>
          {showCancel && (
            <Button
              variant="ghost"
              onClick={() => {
                if (onCancel) {
                  onCancel();
                }
                handleClose();
              }}
            >
              {cancelText || '取消'}
            </Button>
          )}
          <Button
            disabled={loading}
            onClick={async () => {
              if (!onOk) {
                handleClose();
                return;
              }

              setLoading(true);
              try {
                await onOk();
                handleClose();
              } catch (e) {
                console.error(e);
              }
              setLoading(false);
            }}
          >
            {loading && (
              <Loader2Icon className="animate-spin w-4 h-4" />
            )}
            {okText || '确定'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

class ModalDirective {
  private static instance: null | ModalDirective = null;

  private options: ModalDirectiveConfig = {
    title: '',
    content: '',
  };

  private root: Root | undefined;

  static getInstance() {
    if (!ModalDirective.instance) {
      ModalDirective.instance = new ModalDirective();
    }
    return ModalDirective.instance;
  }

  open(config: ModalDirectiveConfig) {
    this.options = { ...this.options, ...config };
    this.createRoot();
    this.render();
  }

  close() {
    this.root?.unmount();
    this.root = undefined;
    const modals = document.getElementsByClassName('choose-material-modal');
    for (let i = 0; i < modals.length; i++) {
      modals[i].remove();
    }
  }

  createRoot() {
    if (!this.root) {
      const div = document.createElement('div');
      div.classList.add('choose-material-modal');
      document.body.appendChild(div);
      this.root = createRoot(div);
    }
  }

  render() {
    const { options } = this;
    const configOnOpenChange = options.onOpenChange;
    this.root?.render(
      <InfoModal
        {...options}
        onOpenChange={(isOpen) => {
          configOnOpenChange?.(isOpen);
          this.close();
        }}
      />,
    );
  }
}

export default ModalDirective.getInstance();
